import { X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import type { FactorEntry, FactorsChartEntityType } from "../services/productFactorsService";
import {
  usePerformanceChartData,
} from "../hooks/usePerformanceChartData";
import {
  STRATEGY_COMPARISON_ALL_KEY,
  STRATEGY_COMPARISON_STRATEGY_KEY,
  type StrategyComparisonProduct,
  useStrategyComparisonChartData,
} from "../hooks/useStrategyComparisonChartData";
import { useActualPerformanceChartData } from "../hooks/useActualPerformanceChartData";
import {
  ACTUAL_COMPARISON_ALL_KEY,
  ACTUAL_COMPARISON_STRATEGY_KEY,
  ACTUAL_COMPARISON_ACTUAL_KEY,
  useStrategyVsActualComparison,
} from "../hooks/useStrategyVsActualComparison";
import { formatSignedPercentage } from "../utils/numberUtils";
import { ALPHA_PERFORMANCE_SERIES_STROKES } from "./performanceChartV3Utils";

import PerformanceChartV3Content from "./PerformanceChartV3Content";
import StrategyComparisonChartContent from "./StrategyComparisonChartContent";
import type { PerformanceChartVisualStyle } from "./performanceChartAppearance";
import DetailNetBenchmarkChart from "../pages/dashboard/components/DetailNetBenchmarkChart";

interface StrategyComparisonChartConfig {
  strategyFactors: FactorEntry[];
  products: StrategyComparisonProduct[];
}

type ModalPerfView = "strategy" | "actual";

interface PerformanceChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: number;
  entityName: string;
  providerId?: number | string;
  forceLightMode?: boolean;
  adminUserId?: string;
  strategyComparison?: StrategyComparisonChartConfig;
  showTodayFilter?: boolean;
  showActualToggle?: boolean;
  initialPerfView?: ModalPerfView;
  actualFactors?: FactorEntry[];
  appearance?: "default" | "alpha";
  visualStyle?: PerformanceChartVisualStyle;
  showBenchmarks?: boolean;
  /**
   * The shown user's `launch_date`. When supplied and not in the future, the
   * standalone strategy and actual charts/tables anchor to it. Passed only by
   * the investor StrategyPage (self) and AdminUserStrategyPage (viewed user);
   * other callers omit it and keep full history. The comparison view receives
   * already-clipped factors via `strategyComparison`/`actualFactors`.
   */
  launchDate?: string | null;
}

type LegacyPerformanceChartModalProps = PerformanceChartModalProps & {
  entityType: "PORTFOLIO" | "BASKET";
  dataSource?: "legacy-metrics";
  entityCreatedAt?: never;
  computedAtUtc?: never;
};

type V3PerformanceChartModalProps = PerformanceChartModalProps & {
  entityType: FactorsChartEntityType;
  dataSource:
    | "v3-factors"
    | "v3-factors-admin"
    | "v3-factors-public"
    | "v3-managed-strategy-factors-admin";
  entityCreatedAt?: string | null;
  computedAtUtc?: string | null;
}

type PerformanceChartModalComponentProps =
  | LegacyPerformanceChartModalProps
  | V3PerformanceChartModalProps;

const LEGACY_TIME_FILTER_OPTIONS = [
  { value: "1 week", label: "1W" },
  { value: "1 month", label: "1M" },
  { value: "3 months", label: "3M" },
  { value: "6 months", label: "6M" },
  { value: "Year to date", label: "YTD" },
  { value: "1 year", label: "1Y" },
  { value: "All", label: "All" },
  { value: "Since inception", label: "Inception" },
];

const EMPTY_COMPARISON_PRODUCTS: StrategyComparisonProduct[] = [];

const PerformanceChartModal: React.FC<PerformanceChartModalComponentProps> = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  entityName,
  providerId,
  forceLightMode = false,
  dataSource = "legacy-metrics",
  entityCreatedAt: propEntityCreatedAt,
  computedAtUtc: propComputedAtUtc,
  adminUserId,
  strategyComparison,
  showTodayFilter = false,
  showActualToggle = false,
  initialPerfView = "strategy",
  actualFactors: propActualFactors,
  launchDate,
  appearance = "default",
  visualStyle = "default",
  showBenchmarks = false,
}) => {
  const { user, effectiveUserId } = useAuth();
  const accountOwnerUserId = effectiveUserId === undefined
    ? user?.id
    : effectiveUserId ?? undefined;
  const isAlpha = appearance === "alpha";
  const [modalPerfView, setModalPerfView] = useState<ModalPerfView>(initialPerfView);
  const isStrategy = entityType === "STRATEGY";
  const entityTypeLabel =
    entityType === "MANAGED_STRATEGY"
      ? "managed strategy"
      : entityType.toLowerCase();
  const comparisonProducts = strategyComparison?.products ?? EMPTY_COMPARISON_PRODUCTS;
  const hasStrategyComparison =
    isStrategy &&
    dataSource !== "legacy-metrics" &&
    comparisonProducts.length > 0;
  const [selectedComparisonKey, setSelectedComparisonKey] = useState<string>(
    STRATEGY_COMPARISON_STRATEGY_KEY
  );
  const [actualComparisonKey, setActualComparisonKey] = useState<string>(
    ACTUAL_COMPARISON_ALL_KEY
  );

  useEffect(() => {
    if (!isOpen) return;
    setModalPerfView(showActualToggle ? initialPerfView : "strategy");
    setSelectedComparisonKey(
      hasStrategyComparison ? STRATEGY_COMPARISON_ALL_KEY : STRATEGY_COMPARISON_STRATEGY_KEY
    );
    setActualComparisonKey(ACTUAL_COMPARISON_ALL_KEY);
  }, [hasStrategyComparison, initialPerfView, isOpen, showActualToggle]);

  useEffect(() => {
    if (!hasStrategyComparison) return;
    if (
      selectedComparisonKey !== STRATEGY_COMPARISON_ALL_KEY &&
      selectedComparisonKey !== STRATEGY_COMPARISON_STRATEGY_KEY &&
      !comparisonProducts.some((product) => product.key === selectedComparisonKey)
    ) {
      setSelectedComparisonKey(STRATEGY_COMPARISON_ALL_KEY);
    }
  }, [comparisonProducts, hasStrategyComparison, selectedComparisonKey]);

  const usesComparisonChart =
    hasStrategyComparison &&
    modalPerfView === "strategy" &&
    selectedComparisonKey !== STRATEGY_COMPARISON_STRATEGY_KEY;

  const chart = usePerformanceChartData({
    isActive: isOpen && !usesComparisonChart && !(isStrategy && modalPerfView === "actual"),
    entityId,
    entityType,
    entityName,
    providerId,
    forceLightMode: forceLightMode || isAlpha || visualStyle === "retro",
    visualStyle,
    dataSource,
    entityCreatedAt: propEntityCreatedAt,
    computedAtUtc: propComputedAtUtc,
    adminUserId,
    showTodayFilter,
    launchDate,
    seriesColor: isAlpha && isStrategy
      ? ALPHA_PERFORMANCE_SERIES_STROKES.strategy
      : undefined,
  });

  const comparisonChart = useStrategyComparisonChartData({
    isActive: isOpen && usesComparisonChart,
    selectedKey: selectedComparisonKey,
    strategyFactors: strategyComparison?.strategyFactors ?? [],
    products: comparisonProducts,
    showTodayFilter,
  });

  const isActualMode = isOpen && isStrategy && showActualToggle && modalPerfView === "actual";
  const usesActualComparisonChart = isActualMode;

  const actualChart = useActualPerformanceChartData({
    isActive: isActualMode,
    adminUserId,
    authenticatedUserId: accountOwnerUserId,
    launchDate,
    isDarkMode: !isAlpha,
  });

  const safeActualFactors = propActualFactors ?? [];
  const actualVsStrategyChart = useStrategyVsActualComparison({
    isActive: usesActualComparisonChart,
    selectedKey: actualComparisonKey,
    strategyFactors: strategyComparison?.strategyFactors ?? [],
    actualFactors: safeActualFactors,
    strategyColor: isAlpha ? ALPHA_PERFORMANCE_SERIES_STROKES.strategy : undefined,
    actualColor: isAlpha ? ALPHA_PERFORMANCE_SERIES_STROKES.actual : undefined,
  });
  const actualComparisonLoading = actualChart.loading || actualVsStrategyChart.loading;
  const actualComparisonError = actualChart.error ?? actualVsStrategyChart.error;

  const productOptions = useMemo(
    () =>
      comparisonProducts.map((product) => (
        <option key={product.key} value={product.key}>
          {product.label}
        </option>
      )),
    [comparisonProducts]
  );

  if (!isOpen) return null;

  if (chart.isV3Factors) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-0 sm:p-4">
          <div
            className={isAlpha
              ? "fixed inset-0 bg-v7-ink/70 backdrop-blur-sm transition-opacity"
              : "fixed inset-0 bg-black bg-opacity-85 transition-opacity"}
            onClick={onClose}
          />

          <div
            data-testid="performance-chart-modal-panel"
            data-appearance={appearance}
            className={`inline-block h-screen w-screen max-w-none transform flex-col overflow-hidden border p-3 text-left align-bottom shadow-xl transition-all sm:h-[94vh] sm:w-[96vw] sm:max-w-[1800px] sm:p-6 ${
              isAlpha
                ? "flex rounded-none border-v7-ink/15 bg-v7-white font-v7-sans text-v7-ink sm:rounded-v7-popover"
                : "flex rounded-none border-slate-800 bg-black sm:rounded-lg"
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex items-center gap-2">
                    <h3 className={`truncate text-lg font-medium leading-6 ${isAlpha ? "text-v7-ink" : "text-white"}`}>
                      {entityName} Performance
                    </h3>
                    {isActualMode && (
                      <span className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        isAlpha ? "bg-v7-bronze text-v7-ink" : "bg-rp-amber text-black"
                      }`}>
                        Beta
                      </span>
                    )}
                  </div>
                  {isStrategy && (
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      {showActualToggle && (
                        <div className={`relative inline-flex items-center rounded-v7-control border p-0.5 ${
                          isAlpha ? "border-v7-ink/20 bg-v7-paper" : "border-gray-700 bg-gray-900"
                        }`}>
                          <div
                            className={`absolute bottom-0.5 top-0.5 rounded-sm transition-all duration-300 ease-in-out ${
                              isAlpha ? "bg-v7-ink" : "bg-rp-amber"
                            }`}
                            style={{
                              width: "calc(50% - 2px)",
                              left: modalPerfView === "strategy" ? "2px" : "calc(50% + 2px)",
                            }}
                          />
                          <button
                            onClick={() => setModalPerfView("strategy")}
                            className={`relative z-10 rounded-sm px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
                              modalPerfView === "strategy"
                                ? isAlpha ? "text-v7-white" : "text-black"
                                : isAlpha ? "text-v7-ink/60 hover:text-v7-ink" : "text-gray-500 hover:text-gray-300"
                            }`}
                          >
                            Strategy
                          </button>
                          <button
                            onClick={() => setModalPerfView("actual")}
                            className={`relative z-10 rounded-sm px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
                              modalPerfView === "actual"
                                ? isAlpha ? "text-v7-white" : "text-black"
                                : isAlpha ? "text-v7-ink/60 hover:text-v7-ink" : "text-gray-500 hover:text-gray-300"
                            }`}
                          >
                            Actual
                          </button>
                        </div>
                      )}
                      {modalPerfView === "actual" && showActualToggle ? (
                        <div className="min-w-0">
                          <label htmlFor="actual-chart-selection" className="sr-only">Chart selection</label>
                          <select
                            id="actual-chart-selection"
                            value={actualComparisonKey}
                            onChange={(event) => setActualComparisonKey(event.target.value)}
                            className={`h-8 w-full max-w-[220px] rounded-v7-control border px-2 text-xs font-semibold transition-colors focus:outline-none focus:ring-1 sm:max-w-[240px] ${
                              isAlpha
                                ? "border-v7-ink/20 bg-v7-canvas text-v7-ink focus:border-v7-bronze-dark focus:ring-v7-bronze-dark"
                                : "border-gray-700 bg-gray-900 text-gray-200 focus:border-rp-amber focus:ring-rp-amber"
                            }`}
                          >
                            <option value={ACTUAL_COMPARISON_ALL_KEY}>All</option>
                            <option value={ACTUAL_COMPARISON_STRATEGY_KEY}>Strategy</option>
                            <option value={ACTUAL_COMPARISON_ACTUAL_KEY}>Actual</option>
                          </select>
                        </div>
                      ) : hasStrategyComparison ? (
                        <div className="min-w-0">
                          <label htmlFor="strategy-chart-selection" className="sr-only">Chart selection</label>
                          <select
                            id="strategy-chart-selection"
                            value={selectedComparisonKey}
                            onChange={(event) => setSelectedComparisonKey(event.target.value)}
                            className={`h-8 w-full max-w-[220px] rounded-v7-control border px-2 text-xs font-semibold transition-colors focus:outline-none focus:ring-1 sm:max-w-[240px] ${
                              isAlpha
                                ? "border-v7-ink/20 bg-v7-canvas text-v7-ink focus:border-v7-bronze-dark focus:ring-v7-bronze-dark"
                                : "border-gray-700 bg-gray-900 text-gray-200 focus:border-rp-amber focus:ring-rp-amber"
                            }`}
                          >
                            <option value={STRATEGY_COMPARISON_ALL_KEY}>All</option>
                            <option value={STRATEGY_COMPARISON_STRATEGY_KEY}>Strategy</option>
                            {productOptions}
                          </select>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close performance chart"
                className={`rounded-v7-control p-2 transition-colors ${isAlpha ? "text-v7-ink/60 hover:bg-v7-paper hover:text-v7-ink" : "text-gray-400 hover:bg-slate-900"}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {isActualMode && usesActualComparisonChart ? (
                <div className="flex flex-col">
                  <StrategyComparisonChartContent
                    loading={actualComparisonLoading}
                    error={actualComparisonError}
                    chartData={actualVsStrategyChart.chartData}
                    lines={actualVsStrategyChart.lines}
                    chartHeight={actualVsStrategyChart.chartHeight}
                    timeFilterOptions={actualVsStrategyChart.timeFilterOptions}
                    availableFilters={actualVsStrategyChart.availableFilters}
                    selectedPeriod={actualVsStrategyChart.selectedPeriod}
                    handleSelectPeriod={actualVsStrategyChart.handleSelectPeriod}
                    isDarkMode={!isAlpha}
                  />
                  {actualVsStrategyChart.actualSummaryFilters.length > 0 && (
                    <div className={`mt-4 border-t pt-4 ${isAlpha ? "border-v7-ink/15" : "border-gray-700"}`}>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {actualVsStrategyChart.actualSummaryFilters.map((filter) => {
                          const perf = actualVsStrategyChart.actualTimeframePerformance[filter];
                          return (
                            <div key={filter} className="text-center">
                              <p className={`mb-0.5 text-[10px] ${isAlpha ? "text-v7-ink/60" : "text-gray-500"}`}>
                                {filter}
                              </p>
                              <p
                                className={`text-sm font-bold tabular-nums ${
                                  perf !== null && perf !== undefined
                                    ? perf >= 0
                                      ? isAlpha ? "text-v7-success" : "text-emerald-400"
                                      : isAlpha ? "text-v7-rust" : "text-red-400"
                                    : isAlpha ? "text-v7-ink/40" : "text-gray-600"
                                }`}
                              >
                                {perf !== null && perf !== undefined
                                  ? formatSignedPercentage(perf, 2)
                                  : "-"}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : isActualMode ? (
                <PerformanceChartV3Content
                  portfolioName={entityName}
                  loading={actualChart.loading}
                  error={actualChart.error}
                  performanceMetrics={actualChart.performanceMetrics}
                  svgRef={actualChart.svgRef}
                  containerRef={actualChart.containerRef}
                  tooltipRef={actualChart.tooltipRef}
                  chartHeight={actualChart.chartHeight}
                  timeFilterOptions={actualChart.timeFilterOptions}
                  availableFilters={actualChart.availableFilters}
                  selectedPeriod={actualChart.selectedPeriod}
                  handleSelectPeriod={actualChart.handleSelectPeriod}
                  formattedCreateDate={actualChart.formattedCreateDate}
                  formatPerformance={actualChart.formatPerformance}
                  formatLocalDate={actualChart.formatLocalDate}
                  orderedFiltersForTable={actualChart.orderedFiltersForTable}
                  timeframePerformance={actualChart.timeframePerformance}
                  v3IntrinsicAvailability={actualChart.v3IntrinsicAvailability}
                  v3LoadedCoverage={actualChart.v3LoadedCoverage}
                  v3HasDataByPeriod={actualChart.v3HasDataByPeriod}
                  entityType={actualChart.entityType}
                  isDarkMode={!isAlpha}
                  appearance={appearance}
                />
              ) : usesComparisonChart ? (
                <StrategyComparisonChartContent
                  loading={comparisonChart.loading}
                  error={comparisonChart.error}
                  chartData={comparisonChart.chartData}
                  lines={comparisonChart.lines}
                  chartHeight={comparisonChart.chartHeight}
                  timeFilterOptions={comparisonChart.timeFilterOptions}
                  availableFilters={comparisonChart.availableFilters}
                  selectedPeriod={comparisonChart.selectedPeriod}
                  handleSelectPeriod={comparisonChart.handleSelectPeriod}
                  isDarkMode={!isAlpha}
                />
              ) : showBenchmarks ? (
                <div className="mx-auto w-full max-w-[900px] bg-white p-2 text-gray-900">
                  <DetailNetBenchmarkChart
                    portfolioName={entityName}
                    chart={chart}
                    visualStyle={visualStyle}
                    showNet={false}
                    showWatermark
                    isDarkMode={false}
                  />
                </div>
              ) : (
                <PerformanceChartV3Content
                  portfolioName={entityName}
                  loading={chart.loading}
                  error={chart.error}
                  performanceMetrics={chart.performanceMetrics}
                  visualStyle={visualStyle}
                  chartData={chart.chartData}
                  svgRef={chart.svgRef}
                  containerRef={chart.containerRef}
                  tooltipRef={chart.tooltipRef}
                  chartHeight={chart.chartHeight}
                  timeFilterOptions={chart.timeFilterOptions}
                  availableFilters={chart.availableFilters}
                  selectedPeriod={chart.selectedPeriod}
                  handleSelectPeriod={chart.handleSelectPeriod}
                  formattedCreateDate={chart.formattedCreateDate}
                  formatPerformance={chart.formatPerformance}
                  formatLocalDate={chart.formatLocalDate}
                  orderedFiltersForTable={chart.orderedFiltersForTable}
                  timeframePerformance={chart.timeframePerformance}
                  v3IntrinsicAvailability={chart.v3IntrinsicAvailability}
                  v3LoadedCoverage={chart.v3LoadedCoverage}
                  v3HasDataByPeriod={chart.v3HasDataByPeriod}
                  entityType={chart.entityType}
                  isDarkMode={!isAlpha && visualStyle !== "retro"}
                  appearance={appearance}
                />
              )}

              {isActualMode && (
                <p className={`mt-6 text-center text-[11px] leading-relaxed ${isAlpha ? "text-v7-ink-soft" : "text-white"}`}>
                  Actual performance may differ from computed strategy
                  performance. Frequent strategy changes, along with exclusions,
                  restrictions, fills, and commissions, can all contribute to
                  these differences.
                </p>
              )}
            </div>

            <div className="mt-4 sm:mt-6 flex justify-end">
              <button
                onClick={onClose}
                className={isAlpha
                  ? "min-h-10 rounded-v7-control border border-v7-ink/30 bg-v7-white px-4 text-sm font-medium text-v7-ink transition-colors hover:border-v7-ink"
                  : "rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-600"}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div
          className={`inline-block align-bottom ${
            chart.isDarkMode ? "bg-gray-800" : "bg-white"
          } rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6`}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg leading-6 font-medium ${chart.isDarkMode ? "text-white" : "text-gray-900"}`}>
              {entityName} Performance
            </h3>
            <button
              onClick={onClose}
              className={`rounded-md p-2 ${chart.isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} transition-colors`}
            >
              <X className={`w-5 h-5 ${chart.isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
            </button>
          </div>

          {chart.loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              <span className={`ml-3 ${chart.isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                Loading performance data...
              </span>
            </div>
          ) : chart.error ? (
            <div
              className={`p-4 rounded-lg ${
                chart.isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
              }`}
            >
              <p className={`${chart.isDarkMode ? "text-red-400" : "text-red-600"}`}>{chart.error}</p>
            </div>
          ) : chart.performanceMetrics.length === 0 ? (
            <div className={`p-8 text-center ${chart.isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              <p>No performance data available for this {entityTypeLabel}.</p>
            </div>
          ) : (
            <div>
              <div className={`${chart.isDarkMode ? "bg-gray-750" : "bg-gray-50"} rounded-lg p-4 sm:p-6`}>
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h4 className={`text-sm font-medium ${chart.isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    Performance Over Time
                  </h4>
                </div>

                <div
                  ref={chart.containerRef}
                  style={{ width: "100%", height: chart.chartHeight, position: "relative", overflow: "visible" }}
                >
                  <svg ref={chart.svgRef} style={{ display: "block" }} />
                  <div
                    ref={chart.tooltipRef}
                    style={{
                      position: "absolute",
                      display: "none",
                      backgroundColor: chart.isDarkMode ? "#1f2937" : "#ffffff",
                      border: `1px solid ${chart.isDarkMode ? "#374151" : "#e5e7eb"}`,
                      borderRadius: "0.375rem",
                      padding: "8px 12px",
                      pointerEvents: "none",
                      zIndex: 1000,
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    }}
                  />
                  <div
                    className="absolute left-2 flex items-center gap-x-[6px] pointer-events-none select-none"
                    style={{ opacity: 0.14, bottom: 36 }}
                  >
                    <img src="/favicon.png" alt="Plutus" className="h-9 w-9" />
                    <img src="/logo.png" alt="Plutus" className="h-7 w-auto" />
                  </div>
                </div>

                <div className="px-3 mt-0">
                  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-medium">
                    {LEGACY_TIME_FILTER_OPTIONS.filter(item => (chart.availableFilters as string[]).includes(item.value)).map(item => (
                      <button
                        key={item.value}
                        onClick={() => chart.handleSelectPeriod(item.value)}
                        aria-pressed={chart.selectedPeriod === item.value}
                        className={`px-2.5 py-1 rounded-full transition-colors border focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
                          chart.selectedPeriod === item.value
                            ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                            : chart.isDarkMode
                            ? "text-gray-300 hover:text-teal-400 hover:bg-gray-700 border-transparent"
                            : "text-gray-600 hover:text-teal-700 hover:bg-teal-50 border-transparent"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                {chart.formattedCreateDate && (
                  <div className={`mt-2 text-center text-[11px] ${chart.isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Inception: {chart.formatLocalDate(chart.formattedCreateDate)}
                  </div>
                )}

                {chart.performanceMetrics.length > 0 && (
                  <div className={`mt-6 pt-6 border-t ${chart.isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {chart.orderedFiltersForTable.map(filter => {
                        const perf = chart.timeframePerformance[filter];
                        return (
                          <div key={filter} className="text-center">
                            <p className={`text-xs ${chart.isDarkMode ? "text-gray-400" : "text-gray-600"} mb-1`}>{filter}</p>
                            <p
                              className={`text-base font-bold ${
                                perf !== null && perf !== undefined
                                  ? perf >= 0
                                    ? "text-teal-600"
                                    : "text-red-600"
                                  : chart.isDarkMode
                                  ? "text-gray-500"
                                  : "text-gray-400"
                              }`}
                            >
                              {perf !== null && perf !== undefined ? chart.formatPerformance(perf) : "-"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                chart.isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              } transition-colors`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceChartModal;
